// File: src/data/countries.js
// Every country a series, an event, an account or an entry can belong to.
//
// The full ISO 3166-1 alpha-2 set (249 codes) plus Kosovo. Territories are
// included deliberately - the Faroes, Gibraltar and Hong Kong all field their
// own entrants, and the old twenty-country list could not express any of them.
//
// WHY THIS IS A CHECKED-IN TABLE AND NOT DERIVED FROM Intl.DisplayNames:
// the browser resolves ~280 two-letter codes, and among them are dead states
// silently mapped to their successors - SU to Russia, DD to Germany, YU to
// Serbia, AN to Curacao - plus UK, a duplicate of GB, and EU and EZ, which are
// not countries. Deriving membership from ICU would put the Soviet Union in the
// picker twice under two names. ICU is used for DISPLAY NAMES only; what counts
// as a country is decided here, by hand, and pinned by a test.
//
// The English `name` is only a fallback for browsers without Intl.DisplayNames
// (see utils/countryNames.js) - every visible name comes from the browser in the
// language being read, which is why no name here needs translating.
//
// KEEP IN SYNC WITH backend/countries.js -- backend/test/unit/
// country-codes.test.js reads this file and fails if the two disagree.

// XK is Kosovo. It is user-assigned rather than officially ISO 3166-1, and is
// here as a deliberate exception - flag-icons ships fi-xk and every browser
// names it. XX, by contrast, must NEVER appear: four backend tests use it as
// their "invalid country" sentinel.

// [code, English name (fallback only), continent]
const COUNTRY_ROWS = [

  // --- nordic (12) ---
  // Pinned to the top of every picker: this is where nearly every entry,
  // series and event actually is. Nordic Council members and their
  // territories - Aland, the Faroes, Greenland, Svalbard - live here rather
  // than under Europe or North America, and appear ONLY here.
  ['AX', 'Åland Islands', 'nordic'],
  ['DK', 'Denmark', 'nordic'],
  ['EE', 'Estonia', 'nordic'],
  ['FI', 'Finland', 'nordic'],
  ['FO', 'Faroe Islands', 'nordic'],
  ['GL', 'Greenland', 'nordic'],
  ['IS', 'Iceland', 'nordic'],
  ['LT', 'Lithuania', 'nordic'],
  ['LV', 'Latvia', 'nordic'],
  ['NO', 'Norway', 'nordic'],
  ['SE', 'Sweden', 'nordic'],
  ['SJ', 'Svalbard & Jan Mayen', 'nordic'],

  // --- europe (42) ---
  // Turkey, Russia, Cyprus and the Caucasus states sit here rather than in
  // Asia: they are where European motorsport federations put them, which is
  // what an organizer filling this in expects.
  ['AD', 'Andorra', 'europe'],
  ['AL', 'Albania', 'europe'],
  ['AT', 'Austria', 'europe'],
  ['BA', 'Bosnia & Herzegovina', 'europe'],
  ['BE', 'Belgium', 'europe'],
  ['BG', 'Bulgaria', 'europe'],
  ['BY', 'Belarus', 'europe'],
  ['CH', 'Switzerland', 'europe'],
  ['CY', 'Cyprus', 'europe'],
  ['CZ', 'Czechia', 'europe'],
  ['DE', 'Germany', 'europe'],
  ['ES', 'Spain', 'europe'],
  ['FR', 'France', 'europe'],
  ['GB', 'United Kingdom', 'europe'],
  ['GG', 'Guernsey', 'europe'],
  ['GI', 'Gibraltar', 'europe'],
  ['GR', 'Greece', 'europe'],
  ['HR', 'Croatia', 'europe'],
  ['HU', 'Hungary', 'europe'],
  ['IE', 'Ireland', 'europe'],
  ['IM', 'Isle of Man', 'europe'],
  ['IT', 'Italy', 'europe'],
  ['JE', 'Jersey', 'europe'],
  ['LI', 'Liechtenstein', 'europe'],
  ['LU', 'Luxembourg', 'europe'],
  ['MC', 'Monaco', 'europe'],
  ['MD', 'Moldova', 'europe'],
  ['ME', 'Montenegro', 'europe'],
  ['MK', 'North Macedonia', 'europe'],
  ['MT', 'Malta', 'europe'],
  ['NL', 'Netherlands', 'europe'],
  ['PL', 'Poland', 'europe'],
  ['PT', 'Portugal', 'europe'],
  ['RO', 'Romania', 'europe'],
  ['RS', 'Serbia', 'europe'],
  ['RU', 'Russia', 'europe'],
  ['SI', 'Slovenia', 'europe'],
  ['SK', 'Slovakia', 'europe'],
  ['SM', 'San Marino', 'europe'],
  ['UA', 'Ukraine', 'europe'],
  ['VA', 'Vatican City', 'europe'],
  ['XK', 'Kosovo', 'europe'],

  // --- africa (59) ---
  // Territories included - Western Sahara, Mayotte, Reunion, Saint Helena.
  ['AO', 'Angola', 'africa'],
  ['BF', 'Burkina Faso', 'africa'],
  ['BI', 'Burundi', 'africa'],
  ['BJ', 'Benin', 'africa'],
  ['BW', 'Botswana', 'africa'],
  ['CD', 'Congo (Kinshasa)', 'africa'],
  ['CF', 'Central African Republic', 'africa'],
  ['CG', 'Congo (Brazzaville)', 'africa'],
  ['CI', 'Cote d\'Ivoire', 'africa'],
  ['CM', 'Cameroon', 'africa'],
  ['CV', 'Cape Verde', 'africa'],
  ['DJ', 'Djibouti', 'africa'],
  ['DZ', 'Algeria', 'africa'],
  ['EG', 'Egypt', 'africa'],
  ['EH', 'Western Sahara', 'africa'],
  ['ER', 'Eritrea', 'africa'],
  ['ET', 'Ethiopia', 'africa'],
  ['GA', 'Gabon', 'africa'],
  ['GH', 'Ghana', 'africa'],
  ['GM', 'Gambia', 'africa'],
  ['GN', 'Guinea', 'africa'],
  ['GQ', 'Equatorial Guinea', 'africa'],
  ['GW', 'Guinea-Bissau', 'africa'],
  ['IO', 'British Indian Ocean Territory', 'africa'],
  ['KE', 'Kenya', 'africa'],
  ['KM', 'Comoros', 'africa'],
  ['LR', 'Liberia', 'africa'],
  ['LS', 'Lesotho', 'africa'],
  ['LY', 'Libya', 'africa'],
  ['MA', 'Morocco', 'africa'],
  ['MG', 'Madagascar', 'africa'],
  ['ML', 'Mali', 'africa'],
  ['MR', 'Mauritania', 'africa'],
  ['MU', 'Mauritius', 'africa'],
  ['MW', 'Malawi', 'africa'],
  ['MZ', 'Mozambique', 'africa'],
  ['NA', 'Namibia', 'africa'],
  ['NE', 'Niger', 'africa'],
  ['NG', 'Nigeria', 'africa'],
  ['RE', 'Réunion', 'africa'],
  ['RW', 'Rwanda', 'africa'],
  ['SC', 'Seychelles', 'africa'],
  ['SD', 'Sudan', 'africa'],
  ['SH', 'Saint Helena', 'africa'],
  ['SL', 'Sierra Leone', 'africa'],
  ['SN', 'Senegal', 'africa'],
  ['SO', 'Somalia', 'africa'],
  ['SS', 'South Sudan', 'africa'],
  ['ST', 'São Tomé & Príncipe', 'africa'],
  ['SZ', 'Eswatini', 'africa'],
  ['TD', 'Chad', 'africa'],
  ['TG', 'Togo', 'africa'],
  ['TN', 'Tunisia', 'africa'],
  ['TZ', 'Tanzania', 'africa'],
  ['UG', 'Uganda', 'africa'],
  ['YT', 'Mayotte', 'africa'],
  ['ZA', 'South Africa', 'africa'],
  ['ZM', 'Zambia', 'africa'],
  ['ZW', 'Zimbabwe', 'africa'],

  // --- asia (50) ---
  // Hong Kong, Macao, Taiwan and Palestine are listed because ISO lists
  // them and they enter races; this is not a statement about anything else.
  ['AE', 'United Arab Emirates', 'asia'],
  ['AF', 'Afghanistan', 'asia'],
  ['AM', 'Armenia', 'asia'],
  ['AZ', 'Azerbaijan', 'asia'],
  ['BD', 'Bangladesh', 'asia'],
  ['BH', 'Bahrain', 'asia'],
  ['BN', 'Brunei', 'asia'],
  ['BT', 'Bhutan', 'asia'],
  ['CN', 'China', 'asia'],
  ['GE', 'Georgia', 'asia'],
  ['HK', 'Hong Kong', 'asia'],
  ['ID', 'Indonesia', 'asia'],
  ['IL', 'Israel', 'asia'],
  ['IN', 'India', 'asia'],
  ['IQ', 'Iraq', 'asia'],
  ['IR', 'Iran', 'asia'],
  ['JO', 'Jordan', 'asia'],
  ['JP', 'Japan', 'asia'],
  ['KG', 'Kyrgyzstan', 'asia'],
  ['KH', 'Cambodia', 'asia'],
  ['KP', 'North Korea', 'asia'],
  ['KR', 'South Korea', 'asia'],
  ['KW', 'Kuwait', 'asia'],
  ['KZ', 'Kazakhstan', 'asia'],
  ['LA', 'Laos', 'asia'],
  ['LB', 'Lebanon', 'asia'],
  ['LK', 'Sri Lanka', 'asia'],
  ['MM', 'Myanmar (Burma)', 'asia'],
  ['MN', 'Mongolia', 'asia'],
  ['MO', 'Macao', 'asia'],
  ['MV', 'Maldives', 'asia'],
  ['MY', 'Malaysia', 'asia'],
  ['NP', 'Nepal', 'asia'],
  ['OM', 'Oman', 'asia'],
  ['PH', 'Philippines', 'asia'],
  ['PK', 'Pakistan', 'asia'],
  ['PS', 'Palestine', 'asia'],
  ['QA', 'Qatar', 'asia'],
  ['SA', 'Saudi Arabia', 'asia'],
  ['SG', 'Singapore', 'asia'],
  ['SY', 'Syria', 'asia'],
  ['TH', 'Thailand', 'asia'],
  ['TJ', 'Tajikistan', 'asia'],
  ['TL', 'Timor-Leste', 'asia'],
  ['TM', 'Turkmenistan', 'asia'],
  ['TR', 'Türkiye', 'asia'],
  ['TW', 'Taiwan', 'asia'],
  ['UZ', 'Uzbekistan', 'asia'],
  ['VN', 'Vietnam', 'asia'],
  ['YE', 'Yemen', 'asia'],

  // --- northAmerica (40) ---
  // Central America and the Caribbean are here rather than in a group of
  // their own - splitting them would add a group almost nobody scrolls to.
  ['AG', 'Antigua & Barbuda', 'northAmerica'],
  ['AI', 'Anguilla', 'northAmerica'],
  ['AW', 'Aruba', 'northAmerica'],
  ['BB', 'Barbados', 'northAmerica'],
  ['BL', 'St. Barthélemy', 'northAmerica'],
  ['BM', 'Bermuda', 'northAmerica'],
  ['BQ', 'Caribbean Netherlands', 'northAmerica'],
  ['BS', 'Bahamas', 'northAmerica'],
  ['BZ', 'Belize', 'northAmerica'],
  ['CA', 'Canada', 'northAmerica'],
  ['CR', 'Costa Rica', 'northAmerica'],
  ['CU', 'Cuba', 'northAmerica'],
  ['CW', 'Curaçao', 'northAmerica'],
  ['DM', 'Dominica', 'northAmerica'],
  ['DO', 'Dominican Republic', 'northAmerica'],
  ['GD', 'Grenada', 'northAmerica'],
  ['GP', 'Guadeloupe', 'northAmerica'],
  ['GT', 'Guatemala', 'northAmerica'],
  ['HN', 'Honduras', 'northAmerica'],
  ['HT', 'Haiti', 'northAmerica'],
  ['JM', 'Jamaica', 'northAmerica'],
  ['KN', 'St. Kitts & Nevis', 'northAmerica'],
  ['KY', 'Cayman Islands', 'northAmerica'],
  ['LC', 'St. Lucia', 'northAmerica'],
  ['MF', 'St. Martin', 'northAmerica'],
  ['MQ', 'Martinique', 'northAmerica'],
  ['MS', 'Montserrat', 'northAmerica'],
  ['MX', 'Mexico', 'northAmerica'],
  ['NI', 'Nicaragua', 'northAmerica'],
  ['PA', 'Panama', 'northAmerica'],
  ['PM', 'St. Pierre & Miquelon', 'northAmerica'],
  ['PR', 'Puerto Rico', 'northAmerica'],
  ['SV', 'El Salvador', 'northAmerica'],
  ['SX', 'Sint Maarten', 'northAmerica'],
  ['TC', 'Turks & Caicos Islands', 'northAmerica'],
  ['TT', 'Trinidad & Tobago', 'northAmerica'],
  ['US', 'United States', 'northAmerica'],
  ['VC', 'Saint Vincent and the Grenadines', 'northAmerica'],
  ['VG', 'British Virgin Islands', 'northAmerica'],
  ['VI', 'U.S. Virgin Islands', 'northAmerica'],

  // --- southAmerica (14) ---
  ['AR', 'Argentina', 'southAmerica'],
  ['BO', 'Bolivia', 'southAmerica'],
  ['BR', 'Brazil', 'southAmerica'],
  ['CL', 'Chile', 'southAmerica'],
  ['CO', 'Colombia', 'southAmerica'],
  ['EC', 'Ecuador', 'southAmerica'],
  ['FK', 'Falkland Islands', 'southAmerica'],
  ['GF', 'French Guiana', 'southAmerica'],
  ['GY', 'Guyana', 'southAmerica'],
  ['PE', 'Peru', 'southAmerica'],
  ['PY', 'Paraguay', 'southAmerica'],
  ['SR', 'Suriname', 'southAmerica'],
  ['UY', 'Uruguay', 'southAmerica'],
  ['VE', 'Venezuela', 'southAmerica'],

  // --- oceania (28) ---
  ['AS', 'American Samoa', 'oceania'],
  ['AU', 'Australia', 'oceania'],
  ['CC', 'Cocos (Keeling) Islands', 'oceania'],
  ['CK', 'Cook Islands', 'oceania'],
  ['CX', 'Christmas Island', 'oceania'],
  ['FJ', 'Fiji', 'oceania'],
  ['FM', 'Micronesia', 'oceania'],
  ['GU', 'Guam', 'oceania'],
  ['KI', 'Kiribati', 'oceania'],
  ['MH', 'Marshall Islands', 'oceania'],
  ['MP', 'Northern Mariana Islands', 'oceania'],
  ['NC', 'New Caledonia', 'oceania'],
  ['NF', 'Norfolk Island', 'oceania'],
  ['NR', 'Nauru', 'oceania'],
  ['NU', 'Niue', 'oceania'],
  ['NZ', 'New Zealand', 'oceania'],
  ['PF', 'French Polynesia', 'oceania'],
  ['PG', 'Papua New Guinea', 'oceania'],
  ['PN', 'Pitcairn Islands', 'oceania'],
  ['PW', 'Palau', 'oceania'],
  ['SB', 'Solomon Islands', 'oceania'],
  ['TK', 'Tokelau', 'oceania'],
  ['TO', 'Tonga', 'oceania'],
  ['TV', 'Tuvalu', 'oceania'],
  ['UM', 'U.S. Outlying Islands', 'oceania'],
  ['VU', 'Vanuatu', 'oceania'],
  ['WF', 'Wallis & Futuna', 'oceania'],
  ['WS', 'Samoa', 'oceania'],

  // --- antarctica (5) ---
  // Last, and effectively decorative. The set is the full ISO list, so
  // these are here for completeness rather than because anyone races there.
  ['AQ', 'Antarctica', 'antarctica'],
  ['BV', 'Bouvet Island', 'antarctica'],
  ['GS', 'South Georgia & South Sandwich Islands', 'antarctica'],
  ['HM', 'Heard & McDonald Islands', 'antarctica'],
  ['TF', 'French Southern Territories', 'antarctica'],
];

// A flag emoji is the two letters as regional indicator symbols, so it is
// derived rather than stored 250 times. Same for the flag-icons class, which is
// always the lower-cased code.
//
// Note both are cosmetic: Windows renders no flag emoji at all and shows the
// letters instead. That was already true of the old twenty and is why
// SeriesManagement uses the flag-icons CSS span rather than the emoji.
const flagEmoji = (code) =>
  String.fromCodePoint(...[...code].map(letter => 0x1F1E6 + letter.charCodeAt(0) - 65));

export const countries = COUNTRY_ROWS.map(([code, name, continent]) => ({
  code,
  name,
  continent,
  flag: flagEmoji(code),
  flagClass: `fi-${code.toLowerCase()}`
}));

// The order groups are shown in. `nordic` first because that is where the
// racing is; `antarctica` last because it is there for completeness. The six
// in between are sorted by their translated label at render time - a fixed
// order here would be right in one language and wrong in the other two.
export const CONTINENT_FIRST = 'nordic';
export const CONTINENT_LAST = 'antarctica';
export const CONTINENTS = [...new Set(countries.map(c => c.continent))];

// Looking a country up by code used to be countries.find(), which was free at
// twenty and is not at 250 - two call sites do it once per rendered row.
const byCode = new Map(countries.map(country => [country.code, country]));

/** The country with this code, or undefined. Callers rely on the undefined. */
export const getCountry = (code) => byCode.get(code);

export default countries;
